"use client";
import {
  ChevronRight,
  Dot,
  ThumbsUp,
  TriangleAlert,
  Camera,
} from "lucide-react";
import Link from "next/link";
import { useState, useCallback, useEffect, memo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import type { ReviewListItem } from "@/app/lib/features/productReview/productReview.types";
import SwiperButton from "../../elements/buttons/SwiperButton";
import { Button } from "../../shadcn/button";
import StarRating from "../../elements/StarRating";
import { trackProductClick } from "@/app/lib/tracking/interactions";
import { useTrackGenericView } from "@/app/hooks/useTrackView";

interface ProductReviewsSectionProps {
  /**
   * Sunucuda çekilmiş ilk sayfa yorumları (page.tsx). Redux boşken ekrana bunlar
   * basılıyor — böylece bölüm İLK HTML'de dolu geliyor ve footer kaymıyor.
   * Pencere sunucuda `VISIBLE_REVIEW_WINDOW` ile bu bölümün istediği sayıya
   * (per_page=10) kesiliyor, aksi hâlde SSR 15 basıp fetch 10'a düşürürdü.
   */
  initialReviews?: ReviewListItem[];
}

const ProductReviewsSection = memo(({ initialReviews }: ProductReviewsSectionProps) => {
  const { product } = useProductDetail();
  const {
    reviewsList,
    isReviewsListLoading,
    reviewsListStatus,
    fetchProductReviewsList,
  } = useProductReview();

  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);

  const productSlug = product?.slug;
  const trackRef = useTrackGenericView("product_reviews_section");

  const updateGradients = useCallback((swiper: SwiperType): void => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  }, []);

  const hasSsrReviews = !!initialReviews?.length;

  useEffect(() => {
    if (!productSlug || reviewsListStatus !== "idle") return;
    // Sunucu aynı pencereyi (page=1, per_page=10) zaten getirdiyse ikinci kez
    // çekmiyoruz: ekranda değişen bir şey olmaz, yalnızca her ürün sayfası
    // görüntülemesinde karşılıksız bir API isteği doğardı. SSR isteği hata
    // verip boş dönerse bu koşul tutmaz ve client fetch yedek olarak çalışır.
    if (hasSsrReviews) return;

    fetchProductReviewsList(productSlug, { page: 1, per_page: 10 });
  }, [productSlug, reviewsListStatus, fetchProductReviewsList, hasSsrReviews]);

  if (!product) return null;

  // Redux doluysa o, değilse SSR verisi. Sıra önemli: kullanıcı istemci tarafında
  // başka bir ürüne geçtiğinde Redux tazelenir ve SSR verisinin önüne geçer.
  const displayReviews =
    reviewsList && reviewsList.length > 0 ? reviewsList : (initialReviews ?? []);
  const hasReviews = displayReviews.length > 0;

  return (
    <section ref={trackRef} className="space-y-4 md:space-y-6" id="product-reviews">
      <h2 className="text-base sm:text-xl font-medium mb-3!">
        Ürün Değerlendirmeleri
      </h2>
      <div className="p-4 sm:p-6 bg-[#fff9eb] rounded-md flex flex-col sm:flex-row items-center gap-3">
        <div className="flex items-center gap-1 sm:gap-2">
          <p className="text-base sm:text-2xl font-medium">
            {product.rating?.average || 0}
          </p>
          <StarRating
            rating={product.rating?.average || 0}
            size="lg"
            className="sm:scale-110"
          />
        </div>
        <Dot size={20} className="sm:w-6 sm:h-6 hidden sm:block" />
        <Link
          href={`/urun-degerlendirmesi/${product.slug}`}
          onClick={() => trackProductClick(product.id)}
          className="flex items-center gap-1 sm:gap-2 group transition-all duration-300 ease-in-out hover:text-main"
        >
          <p className="text-base sm:text-xl font-medium">
            {product.rating?.count || 0}
          </p>
          <div className="flex items-center gap-1">
            <p className="text-base sm:text-xl text-muted-foreground transition-all duration-300 ease-in-out group-hover:text-main">
              {product.rating?.text || "Değerlendirme"}
            </p>
            <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground transition-all duration-300 ease-in-out group-hover:text-main" />
          </div>
        </Link>
      </div>
      {/* VERİ ÖNCELİKLİ sıra: gösterilecek yorum varsa yükleme metni hiç
          basılmaz. Ters sırada olsaydı, SSR'dan gelen dolu swiper client fetch
          başladığı anda tek satırlık "yükleniyor" metniyle yer değiştirir ve
          düzelttiğimiz kaymayı geri getirirdi. */}
      {hasReviews ? (
        <Swiper
          spaceBetween={8}
          slidesPerView={1.1}
          onSwiper={updateGradients}
          onSlideChange={updateGradients}
          breakpoints={{
            375: { slidesPerView: 1.2, spaceBetween: 8 },
            480: { slidesPerView: 1.5, spaceBetween: 10 },
            640: { slidesPerView: 2.2, spaceBetween: 10 },
            768: { slidesPerView: 2.5, spaceBetween: 12 },
            900: { slidesPerView: 2.8, spaceBetween: 12 },
            1024: { slidesPerView: 3.2, spaceBetween: 12 },
            1200: { slidesPerView: 3.5, spaceBetween: 8 },
          }}
        >
          {displayReviews.map((review) => {
            const createdAtInfo = review.info.find(
              (info) => info.key === "created_at",
            );
            return (
              <SwiperSlide key={review.id} className="h-auto!">
                <div className="border p-3 sm:p-4 rounded-md h-full flex flex-col min-h-45">
                  <StarRating rating={review.rating} size="sm" />
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mt-1">
                    <p>{review.review_customer_info}</p>
                    <Dot size={16} />
                    <p>{createdAtInfo?.value || ""}</p>
                  </div>
                  <div className="text-xs sm:text-sm mt-3 sm:mt-5 line-clamp-3 flex-1">
                    {review.comment}
                  </div>
                  <div className="mt-3 sm:mt-4 flex items-center justify-between">
                    <p className="text-muted-foreground text-xs font-medium">
                      <span className="text-[#4d8ee1]">
                        {review.seller.name}
                      </span>{" "}
                      satıcısından alındı
                    </p>
                    <div className="flex item-center text-muted-foreground">
                      <button
                        type="button"
                        className="cursor-pointer transition-all duration-300 ease-in-out hover:text-green-500"
                      >
                        <ThumbsUp size={16} />
                      </button>
                      <Dot size={16} />
                      <button
                        type="button"
                        className="cursor-pointer transition-all duration-300 ease-in-out hover:text-red-500"
                      >
                        <TriangleAlert size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
          <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden xl:flex">
            <SwiperButton position="left" slideMode="group" />
            <SwiperButton position="right" slideMode="group" />
          </div>
          <div
            className={`absolute top-0 left-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
              showLeftGradient ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`absolute top-0 right-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${
              showRightGradient ? "opacity-100" : "opacity-0"
            }`}
          />
        </Swiper>
      ) : isReviewsListLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Yorumlar yükleniyor...
        </div>
      ) : null}
      {hasReviews && (
        <div className="relative flex items-center justify-center mt-4">
          <Button
            variant="outline"
            className="rounded-full px-6 sm:px-10"
            asChild
          >
            <Link
              href={`/urun-degerlendirmesi/${product.slug}`}
              onClick={() => trackProductClick(product.id)}
            >
              TÜM DEĞERLENDİRMELERİ GÖSTER
              <ChevronRight size={18} className="sm:w-5 sm:h-5" />
            </Link>
          </Button>
          <div className="absolute left-0 top-1/2 translate-y-1/2 w-full h-px bg-[#e5e5e5] -z-10" />
        </div>
      )}
    </section>
  );
});

ProductReviewsSection.displayName = "ProductReviewsSection";

export default ProductReviewsSection;
